<template>
  <div>
    <Navbar />
    <Breadcrumb />
    <div class="container-xl mb-4">
      <div class="text-container">
        <div class="title-area">
          <div
            class="d-flex"
          >
            <div class="survey-type-badge btn-primary mr-2">
              <fa-icon
                icon="copy"
                class="survey-icon"
              />
            </div>
            <div
            >
              <h2 class="pl-4">&nbsp;Aggregated Reports {{ step }}</h2>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="report_id === 0">
            <div v-if="step === 1">
              <div class="row">
                <div class="col-12 mb-2">
                  <h4>Step 1 - Set report information</h4>
                </div>
                <div class="col-12 mb-2">
                  <div class="alert alert-warning" role="alert">
                    <b>Please note:</b> All underlying surveys on which the aggregated report is based must have the same norm data.
                  </div>
                </div>
                <div class="col-12 mb-3">
                  <label class="form-label">Report name</label>
                  <input
                    class="form-control"
                    :class="`${anon ? 'g-anon' : '' }`"
                    v-model="report_name"
                  />
                </div>
                <div class="col-12 mb-3">
                  <label class="form-label">Survey type</label>
                  <select
                    class="form-select"
                    v-model="surveytype_id"
                    @change="language_options_get();normvalues_countries_get();"
                  >
                    <option
                      v-for="option in surveytypes"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </div>
                <div class="col-12 mb-3">
                  <label class="form-label">Norm values to use for the report</label>
                  <select
                    class="form-select"
                    v-model="normvalues_id"
                  >
                    <option
                      v-for="option in normvalues"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </div>
                <div class="col-12 mb-3">
                  <label class="form-label">Report language</label>
                  <select
                    class="form-select"
                    v-model="language_id"
                  >
                    <option
                      v-for="option in languages"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div v-if="step === 2">
              <div class="row">
                <div class="col-12 mb-2">
                  <h4>Step 2 - Select Surveys</h4>
                  Check the box for the surveys you wish to include in the report.
                </div>
                <div class="col-12 mb-2">
                  <h5>Surveys (total: {{ surveyRows }}, selected: {{ surveyselectcount }})</h5>
                </div>
                  <div
                  v-show="consultantgroup_id !== '0'"
                >
                  <div class="form-check ml-4">
                    <input
                      id="show-group-surveys"
                      v-model="showgroupsurveys"
                      class="form-check-input"
                      type="checkbox"
                      @change="showconsultantsingroupsurveys"
                    >
                    <label class="form-check-label" for="show-group-surveys">
                      Show surveys from other consultants in my group
                    </label>
                  </div>
                </div>
                <div
                  v-if="shownormvalueserror"
                  class="alert alert-danger col-12 mb-2"
                  role="alert"
                >
                  <b>Please note:</b> this survey does not have the same norm data as the norm data selected for the report. If you include this survey, the report will make incorrect comparisons.
                </div>
                <div class="gh300 col-12">
                  <BaseTable
                    striped
                    responsive
                    hover
                    :items="surveys"
                    :fields="surveyfields"
                    :busy="loading"
                    no-local-sorting
                  >
                    <template #head(select)>
                      <span class="visually-hidden">Select survey</span>
                    </template>
                    <template #cell(select)="data">
                      <div class="py-2 form-check m-0 d-flex justify-content-center align-items-center" @click.stop>
                        <input
                          :id="`survey-${data.item.id}`"
                          v-model="data.item.selected"
                          class="form-check-input"
                          type="checkbox"
                          value="1"
                          true-value="1"
                          false-value="0"
                          @change="surveys_countselected"
                        >
                        <label
                          class="form-check-label mb-0"
                          :for="`survey-${data.item.id}`"
                        >
                          <span class="visually-hidden">
                            Select {{ data.item.survey_name }}
                          </span>
                        </label>
                      </div>
                    </template>
                    <template #cell(group_id)="data">
                      <span :class="`${anon ? 'g-anon' : '' }`">{{ data.item.group_id }}</span>
                    </template>
                    <template #cell(survey_name)="data">
                      <span :class="`${anon ? 'g-anon' : '' }`">{{ data.item.survey_name }}</span>
                    </template>
                    <template #cell(user_id)="data">
                      <span
                        v-if="isOtherConsultantSurvey(data.item)"
                        class="user-indicator"
                        :title="surveyOwnerTooltip(data.item)"
                      >
                        <fa-icon :icon="['fas', 'user']" />
                      </span>
                      <span v-else class="user-indicator user-indicator--placeholder"></span>
                    </template>
                  </BaseTable>
                </div>
              </div>
            </div>
            <div v-if="step === 3">
              <div class="row">
                <div class="col-12 mb-2">
                  <h4>Step 3 - Select measurement periods for comparison</h4>
                </div>
                <div
                  class="card drop-zone col-12 px-0 mx-3 my-3"
                  style="max-width: 100rem;"
                  @drop="onDrop($event, 0)"
                  @dragover.prevent
                  @dragenter.prevent
                >
                  <div class="card-header text-white bg-secondary">
                    Surveys selected for the aggregated report:
                  </div>
                  <div class="card-body">
                    <div
                      v-for="survey in surveys.filter(s => s.in_timeslot !== 1 && Number(s.selected) === 1)"
                      :key="survey.id"
                      draggable="true"
                      style="cursor: pointer;"
                      @dragstart="startDrag($event, survey)"
                    >
                      <span class="ml-2">
                        {{ survey.survey_end_date }} <span :class="`${anon ? 'g-anon' : '' }`">{{ survey.survey_name }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-12 mb-2 mx-2 my-2">
                  <h5>Add a measurement period:</h5>
                </div>
              </div>
              <div class="row align-items-end">
                <div class="col-12 col-md-7 pl-4">
                  <div class="row align-items-center">
                    <label
                      class="col-form-label col-12 col-md-3 text-md-end mb-2 mb-md-0"
                      for="dateperiod_label"
                    >
                      Label:
                    </label>
                    <div class="col-12 col-md-9">
                      <input
                        id="dateperiod_label"
                        v-model="dateperiod_label"
                        class="form-control"
                        type="text"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4 d-flex align-items-end mt-3 mt-md-0">
                  <button
                    class="btn btn-primary"
                    :class="dateperiod_label === '' ? 'disabled' : ''"
                    :disabled="dateperiod_label === ''"
                    @click="adddateperiod"
                  >
                    <fa-icon
                      icon="check"
                    />
                    Add
                  </button>
                </div>
              </div>
              <div class="row">
                <div class="col-12 mb-2 mx-2 mt-3">
                  <h5>Measurement periods for the report:</h5>
                </div>
              </div>
              <div class="row mb-4">
                <div
                  v-if="dateperiods.length === 0"
                  class="col-12 mb-3 mx-2"
                >
                  No measurement periods have been added
                </div>
                <div
                  v-for="dateperiod in dateperiods"
                  :key="dateperiod.id"
                  class="col-12 col-md-6 d-flex"
                >
                  <div class="card card--flat measurement-card mx-3 w-100">
                    <div class="card-header text-white bg-primary d-flex justify-content-between">
                      <span>{{ dateperiod.label }}</span>
                      <span
                        class="right pull-right text-right right-align float-right"
                        @click="removedateperiod(dateperiod.id)"
                      >
                        <fa-icon
                          icon="trash"
                        />
                      </span>
                    </div>
                    <div
                      class="card-body drop-zone"
                      @drop="onDrop($event, dateperiod.id)"
                      @dragover.prevent
                      @dragenter.prevent
                    >
                      <div
                        v-for="survey in dateperiod.surveys"
                        :key="survey.id"
                        draggable="true"
                        style="cursor: pointer"
                        @dragstart="startDrag($event, survey)"
                      >
                        {{ survey.survey_end_date }} <span :class="`${anon ? 'g-anon' : '' }`">{{ survey.survey_name }}</span>
                      </div>
                      <div v-if="dateperiod.surveys.length === 0">
                        Drag and drop surveys here from the list above
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="invoice_id === 0">
            <h4 v-if="step === 4">
              Step 4 - Summary
            </h4>
            <div v-if="step === 4">
              <div class="alert alert-warning" role="alert">
                <b>Please note:</b> after you have completed the payment in the next step, you can no longer make any changes to the report. Please review the summary information below before proceeding to payment. If everything is correct, click &quot;Next&quot;.
              </div>
              <div class="row mb-4">
                <div class="col-12 mb-2">
                  <h5>Report parameters</h5>
                </div>
                <div class="col-12">
                  You have made the following selections for the report:
                </div>
                <div class="col-3">
                  Report name:
                </div>
                <div class="col-9">
                  <span :class="`${anon ? 'g-anon' : '' }`">{{ report_name }}</span>
                </div>
                <div class="col-3">
                  Survey type:
                </div><div class="col-9">
                  {{ surveytypes.filter(s => s.value === surveytype_id)[0].text }}
                </div>
                <div class="col-3">
                  Norm values to use for the report:
                </div><div class="col-9">
                  {{ normvalues.filter(s => s.value === normvalues_id)[0].text }}
                </div>
                <div class="col-3">
                  Report language:
                </div><div class="col-9">
                  {{ languages.filter(s => s.value === language_id)[0].text }}
                </div>
              </div>
              <div class="row mb-4">
                <div class="col-12 mb-2">
                  <h5>Measurement periods</h5>
                </div>
                <div
                  v-if="dateperiods.length === 0"
                  class="col-12 mb-2 mx-2"
                >
                  No measurement periods have been added
                </div>
                <div
                  v-for="dateperiod in dateperiods"
                  :key="dateperiod.id"
                  class="card mx-4"
                  style="max-width: 40rem;"
                >
                  <div class="card-header text-white bg-primary">
                    {{ dateperiod.label }}
                  </div>
                  <div class="card-body">
                    <div
                      v-for="survey in dateperiod.surveys"
                      :key="survey.id"
                    >
                      {{ survey.survey_end_date }} <span :class="`${anon ? 'g-anon' : '' }`">{{ survey.survey_name }}</span>
                    </div>
                    <div v-if="dateperiod.surveys.length === 0">
                      No available surveys completed in this period
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Payment -->
          <div v-if="step === 5">
            <div v-if="invoice_id !== 0 && invoice_status !== 2">
              <h4>Report Invoice</h4>
              <BaseTable
                id="invoiceheadtable"
                :items="invoicehead"
                :fields="invoiceheadfields"
                class="mb-3"
              >
                <template #cell(value)="data">
                  <span :class="`${anon ? 'g-anon' : '' }`">
                      {{ data.item.value }}
                    </span>
                </template>
              </BaseTable>
              <BaseTable
                id="invoicedetailstable"
                :items="invoicedetails"
                :fields="invoicedetailsfields"
                class="mb-3"
              >
                <template #cell(item_text)="data">
                  <span :class="`${anon ? 'g-anon' : '' }`">
                    {{ data.item.item_text }}
                  </span>
                </template>
              </BaseTable>
              <div class="d-flex justify-content-end text-end mb-4">
                <div>
                  TOTAL SUM<br>
                  <strong>VAT</strong><br>
                  <strong>TOTAL SUM INCLUDING VAT</strong>
                </div>
                <div class="ms-3">
                  {{ invoice.invoice_amount }} SEK<br>
                  <strong>{{ invoice.invoice_vat_amount }} SEK</strong><br>
                  <strong>{{ invoice.invoice_amount_inc_vat }} SEK</strong>
                </div>
              </div>
              <div class="justify-content-center text-center d-flex pb-2">
                <strong>Contact information:&nbsp;&nbsp;</strong>GDQ Associates AB, e-mail:&nbsp;&nbsp;<a href="mailto: gdq@gdq.se">gdq@gdq.se</a>, telephone: +46 76 052 52 03
              </div>
              <div class="justify-content-center text-center d-flex pb-2">
                <a href="https://gdq.se/en/privacypolicy/">Click here to view our privacy policy</a>
              </div>
              <h4>Payment</h4>
              <h5>Invoice payee information</h5>
              <div class="mb-3">
                <label class="form-label">
                  Invoice Reference
                  <b>
                    <span
                      v-if="invoice_reference === ''"
                      class="text-danger"
                    >
                      required
                    </span>
                  </b>
                </label>
                <input
                  class="form-control"
                  :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_reference"
                  placeholder="Invoice reference"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  Invoice Name <b><span
                    v-if="invoice_name === ''"
                    class="text-danger"
                  >required</span></b>
                </label>
                <input
                  class="form-control"
                  v-model="invoice_name"
                  :class="`${anon ? 'g-anon' : '' }`"
                  placeholder="Invoice name"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  E-mail
                </label>
                <input
                  class="form-control"
                  v-model="invoice_email"
                  :class="`${anon ? 'g-anon' : '' }`"
                  placeholder="Invoice e-mail"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  Company <b><span
                    :class="`${anon ? 'g-anon' : '' }`"
                    v-if="invoice_company === ''"
                    class="text-danger"
                  >required</span></b>
                </label>
                <input
                class="form-control"
                :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_company"
                  placeholder="Company name"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  Address
                </label>
                <input
                class="form-control"
                :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_address_1"
                  placeholder="Invoice address line 1"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <input
                class="form-control"
                :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_address_2"
                  placeholder="Invoice address line 2"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  Zip code
                </label>
                <input
                class="form-control col-3"
                :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_zip_code"
                  size="6"
                  maxlength="6"
                  placeholder="Zip code"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  City
                </label>
                <input
                class="form-control"
                :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_city"
                  placeholder="City"
                  @keyup="invoice_updateinfo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">
                  VAT number/social security number <b>
                    <span
                      v-if="invoice_company_regno === ''"
                      class="text-danger"
                    >
                      required
                    </span>
                  </b>
                </label>
                <input
                class="form-control"
                :class="`${anon ? 'g-anon' : '' }`"
                  v-model="invoice_company_regno"
                  placeholder="VAT number/social security number"
                  @keyup="invoice_updateinfo"
                />
                <p></p>
              </div>
              <div class="form-check pb-4">
                <input
                  id="invoice-accept"
                  v-model="invoice_accept"
                  class="form-check-input"
                  type="checkbox"
                >
                <label class="form-check-label" for="invoice-accept">
                  <strong>
                    I accept the
                    <a
                      href="https://gdq.se/sv/kopvillkor/"
                      target="_new"
                    >
                      terms of purchase
                    </a> and the charges as specified above
                  </strong>
                </label>
              </div>
              <div
                v-if="invoice_accept && invoice_name !== '' && invoice_company !== '' && invoice_company_regno !== '' && invoice_reference !== ''"
                class="mb-3"
              >
                <div class="mb-2">I want to pay by:</div>
                <div class="form-check" v-if="canpayInvoice()">
                  <input
                    id="payment-invoice"
                    v-model="payment"
                    class="form-check-input"
                    type="radio"
                    name="payment"
                    value="2"
                  >
                  <label class="form-check-label" for="payment-invoice">
                    Invoice
                  </label>
                </div>
                <div class="form-check">
                  <input
                    id="payment-card"
                    v-model="payment"
                    class="form-check-input"
                    type="radio"
                    name="payment"
                    value="4"
                    >
                    <label class="form-check-label" for="payment-card">
                      Credit Card
                    </label>
                </div>
              </div>
              <!-- Invoice payment -->
              <div v-if="payment === 2">
                <div class="mb-3">
                  <label class="form-label">I want to receive my invoice by:</label>
                  <select
                    class="form-select"
                    v-model="e_invoice"
                    @change="invoice_updateinfo"
                  >
                    <option
                      v-for="option in einvoiceoptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </div>
                The invoice will be sent to the address you have provided. If you already have a link to e-invoices via your organisation, the invoice will be sent via that route. As a part of our environmental management, we encourage digital invoices. Ensure that you have entered your e-mail if you want to receive the invoice by e-mail.
                <div class="text-right align-right mt-4 pt-4">
                  <button
                    class="btn btn-primary btn-lg"
                    :disabled="formok === false"
                    @click.prevent="invoice_save"
                  >
                    <span class="icon-file-presentation mr-2 pl-2" />Continue to the Report
                  </button>
                </div>
              </div>
              <!-- DIBS payment -->
              <div v-if="payment === 1">
                <h5>Card Payment</h5>
                <div class="py-4 col-12 text-center align-center">
                  Payment service provided by DIBS<br>
                  <img
                    class="ml-4"
                    src="https://cdn.dibspayment.com/logo/checkout/combo/horiz/DIBS_checkout_kombo_horizontal_04.png"
                    alt="DIBS - Payments made easy"
                    width="270"
                  >
                </div>
                <form
                  id="gdq_dibsform"
                  ref="gdq_dibsform"
                  class="text-center align-center"
                  method="post"
                  action="https://payment.architrade.com/paymentweb/start.action"
                >
                  <input
                    type="hidden"
                    name="amount"
                    :value="invoice.dibs_amount * 100"
                  >
                  <input
                    type="hidden"
                    name="currency"
                    value="SEK"
                  >
                  <input
                    type="hidden"
                    name="lang"
                    value="en"
                  >
                  <input
                    type="hidden"
                    name="capturenow"
                    value="yes"
                  >
                  <input
                    type="hidden"
                    name="decorator"
                    value="responsive"
                  >
                  <input
                    type="hidden"
                    name="merchant"
                    value="90219297"
                  >
                  <input
                    type="hidden"
                    name="orderid"
                    :value="order_id"
                  >
                  <input
                    type="hidden"
                    name="ordertext"
                    :value="invoice.invoice_text"
                  >
                  <input
                    type="hidden"
                    name="accepturl"
                    :value="accept_url"
                  >
                  <input
                    type="hidden"
                    name="callbackurl"
                    :value="callback_url"
                  >
                  <input
                    type="hidden"
                    name="cancelurl"
                    :value="cancel_url"
                  >
                  <input
                    type="hidden"
                    name="gdqurl"
                    :value="gdq_url"
                  >
                  <input
                    v-if="consultant_id === 1"
                    label="test"
                    type="text"
                    name="test"
                    value="test"
                  >
                  <button
                    class="btn btn-primary btn-lg"
                    @click.prevent.stop="dibs_payment"
                  >
                    <span class="icon-receipt mr-2 pl-2" />Continue to DIBS Payment
                  </button>
                </form>
              </div>
              <!-- NETS Easy Payment -->
              <div v-if="payment === 4">
                <NetsPayment
                  product-name="GDQ Aggregated Report"
                  :dibs-amount="invoice.dibs_amount"
                  :order-id="order_id"
                  :accept-url="web_url"
                  :cancel-url="web_url"
                />
              </div>
            </div>
            <div
              v-if="invoice_id !== 0 && invoice_status === 2"
              class="mx-2 px-2 align-center text-center center"
            >
              <h3 class="mb-4 pb-4">
                <span v-if="report_loaded === 0 || processingpayment === true">
                  <div
                    class="spinner-border text-primary"
                    role="status"
                    style="width: 2rem; height: 2rem;"
                  >
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  Generating your report...
                </span>
                <span v-if="report_loaded === 1 && processingpayment === false">Thank you! Your aggregated report has been generated and will appear in the list &quot;My aggregated reports&quot; on the home page.</span>
              </h3>
              <div class="row">
                <button
                  v-if="report_loaded === 1"
                  class="btn btn-primary mx-auto large col-4 mb-4"
                  @click.prevent="report_download"
                >
                  Download report
                </button>
              </div>
              <div class="row">
                <button
                  v-if="report_loaded === 1"
                  class="btn btn-warning mx-auto large col-4"
                  @click.prevent="receipt_download"
                >
                  Download payment receipt
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="step < 5 && processingpayment === false"
            class="row"
          >
            <div
              class="col-12 d-flex justify-content-between align-items-center pt-4 mt-4"
            >
              <button
                class="btn btn-secondary"
                @click="prevstep"
              >
                &lt; {{ step === 1 ? 'Cancel' : 'Back' }}
              </button>
              <button
                class="btn btn-primary"
                :disabled="stepnotcomplete()"
                @click="nextstep"
              >
                Next >
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
// invoice_status:
// 0 = ?
// 1 =
// 2 =

import Navbar from './Navbar.vue'
import Breadcrumb from './Breadcrumb.vue'
import NetsPayment from './NetsPayment'
import BaseTable from './base/BaseTable.vue'
import { config } from '../../config'
import axios from 'axios'

const surveyfields = [
  { key: 'select', label: ' ', sortable: false, tdClass: 'w-2 text-center select-td', thClass: 'text-center select-th' },
  { key: 'user_id', label: '', sortable: true, tdClass: 'w-1' },
  { key: 'group_id', sortable: true, label: 'Group name', tdClass: 'd-table-cell', thClass: 'd-table-cell' },
  { key: 'survey_name', sortable: true, label: 'Survey name', tdClass: 'd-table-cell', thClass: 'd-table-cell' },
  { key: 'survey_end_date', sortable: true, label: 'Completion date', tdClass: 'd-table-cell', thClass: 'd-table-cell' },
  { key: 'membercount', sortable: true, label: 'Questionnaires', tdClass: 'd-table-cell', thClass: 'd-table-cell' },
  { key: 'completecount', sortable: true, label: 'Replied', tdClass: 'd-table-cell', thClass: 'd-table-cell' }
]

export default {
  name: 'AggregatedReports',
  components: {
    Navbar,
    Breadcrumb,
    NetsPayment,
    BaseTable
  },
  data () {
    return {
      profile: this.$store.state.profile,
      consultant_id: this.$store.state.user_id,
      step: 1,
      groupRows: 0,
      surveyRows: 0,
      report_id: this.$store.state.currentaggregatedreport_id,
      report_name: '',
      surveytypes: [],
      languages: [],
      normvalues: [],
      surveys: [],
      surveyfields,
      surveytype_id: 0,
      language_id: 0,
      normvalues_id: 0,
      surveyselectcount: 0,
      select_start_date: '',
      select_end_date: '',
      dateperiod_label: '',
      dateperiods: [],
      draggedSurveyId: null,
      order_id: 0,
      invoice_id: 0,
      invoice: {},
      invoice_paid: 0,
      invoice_status: 0,
      invoicehead: [],
      invoiceheadfields: [
        { key: 'label', label: '' },
        { key: 'value', label: '', tdClass: 'align-right text-right' }
      ],
      invoicedetails: [],
      invoicedetailsfields: [
        { key: 'item_text', label: 'Report name', sortable: false },
        { key: 'price', label: 'Price', sortable: false, thClass: 'align-right text-right', tdClass: 'align-right text-right' }
      ],
      invoice_reference: '',
      invoice_name: '',
      invoice_email: '',
      invoice_company: '',
      invoice_address_1: '',
      invoice_address_2: '',
      invoice_zip_code: '',
      invoice_city: '',
      invoice_company_regno: '',
      e_invoice: 0,
      einvoiceoptions: [
        { value: '0', text: 'Paper invoice sent by post' },
        { value: '1', text: 'PDF invoice sent by e-mail' },
        { value: '2', text: 'E-invoice' }
      ],
      invoice_accept: false,
      formok: false,
      payment: null,
      base_url: config.base_url,
      web_url: window.location + '',
      report_loaded: 0,
      netspayment_id: this.$route.query.paymentid ? this.$route.query.paymentid : '',
      nets_paid_amount: 0,
      nets_invoice_amount: 0,
      processingpayment: !!this.$route.query.paymentid,
      consultantgroup_id: this.$store.state.profile.consultantgroup_id,
      consultantsingroupids: [],
      showgroupsurveys: false,
      shownormvalueserror: false,
      loading: false
    }
  },
  computed: {
    anon: function () {
      return this.$store.state.anon
    },
    accept_url: function () {
      const url = config.report_url + '/gdq_dibsreturn.php'
      // var url = window.location;
      return url
    },
    cancel_url: function () {
      const url = config.report_url + '/gdq_dibsreturn.php'
      return url
    },
    callback_url: function () {
      const url = this.base_url + '/gdq_dcallback'
      return url
    },
    gdq_url: function () {
      const url = window.location
      return url
    }
  },
  async created () {
    await this.checknetspayment()
    await this.profile_get()
    this.surveytypes_get()
    this.normvalues_countries_get()
    this.language_options_get()
    this.report_get()
    this.consultantsingroup_get()
  },
  methods: {
    async profile_get () {
      const res = await this.$store.dispatch('profile_fetch', {})
      this.consultantgroup_id = res.data.consultantgroup_id
      return res
    },
    async showconsultantsingroupsurveys () {
      await this.consultantsingroup_get()
      this.surveys_get()
    },
    consultantsingroup_get () {
      let consultantsingroup = [this.$store.state.profile]
      this.consultantsingroupids = [this.consultant_id]

      if (this.consultantgroup_id !== '0' && this.showgroupsurveys === true) {
        const filter = {
          consultantgroup_id: {
            operator: '=',
            value: this.consultantgroup_id
          }
        }
        return this.$store.dispatch('records_readall', { table_name: 'g_profile', filter })
          .then((resp) => {
            consultantsingroup = resp.data.result
            this.consultantsingroupids = []
            for (let i = 0; i < consultantsingroup.length; i++) {
              this.consultantsingroupids.push(consultantsingroup[i].id)
            }
          })
      } else {
        return true
      }
    },
    async checknetspayment () {
      if (this.netspayment_id !== '') {
        this.step = 5
        this.processingpayment = true
        // Update the invoice and payment_lof
        await this.$store.dispatch('netspayment_update', { paymentId: this.netspayment_id })
          .then(async (resp) => {
            if (resp.data.status === 'OK') {
            // Remove paymentID
              await this.makereport().then(() => {
                this.$router.push(this.$route.path)
              })
            } else {
              alert(resp.data.error)
            }
            this.processingpayment = false
          })
      }
    },
    startDrag (evt, survey) {
      evt.dataTransfer.dropEffect = 'move'
      evt.dataTransfer.effectAllowed = 'move'
      evt.dataTransfer.setData('text/plain', survey.id)
      evt.dataTransfer.setData('surveyID', survey.id)
      this.draggedSurveyId = survey.id
    },
    onDrop (evt, list) {
      evt.preventDefault()
      const surveyID = evt.dataTransfer.getData('surveyID') || evt.dataTransfer.getData('text/plain') || this.draggedSurveyId
      const targetListId = list
      const survey = this.surveys.find(survey =>
        survey.id.toString() === surveyID.toString() && Number(survey.selected) === 1
      )
      if (!survey) {
        this.draggedSurveyId = null
        return
      }
      delete survey.group_id

      if (targetListId !== 0) {
        const dateperiod = this.dateperiods.find(dateperiod => dateperiod.id.toString() === targetListId.toString())
        if (dateperiod) {
          dateperiod.surveys.push(survey)
          survey.in_timeslot = 1
        }
      }
      this.dateperiods.forEach((p) => {
        const index = p.surveys.findIndex((s) => s.id.toString() === surveyID.toString())
        if (index > -1 && p.id.toString() !== targetListId.toString()) {
          p.surveys.splice(index, 1)
        }
      })
      if (targetListId === 0) {
        survey.in_timeslot = 0
      }
      this.draggedSurveyId = null
    },
    dibs_payment () {
      const payload = { invoice_id: this.invoice_id }
      this.$store.dispatch('paymentlog_create', payload)
        .then(() => {
          // ---------------------
          // Here we POST to DIBS
          // ---------------------
          this.$refs.gdq_dibsform.submit()
        })
    },
    // Create or get the invoice
    // ----------------------------------------------------------------
    // Get/create invoice for report
    // invoice_status:
    // 0 = There are invoices to be paid/handled
    // 1 = Not used
    // 2 = All invoices created and payment selected
    // report_paid:
    // 0 = We have not done the payment processing to end
    // 1 = The payment processing is finished (inv or return from DIBS)
    // report_loaded:
    // 0 = The report has not been loaded
    // 1 = The report has been loaded
    // ----------------------------------------------------------------
    invoice_get () {
      // This on returns an existing invoice or a newly created one
      // invoice_paid = 0 - Newly created invoice or unpaid previous invoice
      // invoice_paid = 1 - Hunky dory
      this.$store.dispatch('aggregatedinvoice_fetch', { report_id: this.report_id })
        .then((resp) => {
          this.invoice_id = resp.data.invoice_id
          this.invoice = resp.data.invoice
          this.invoice_paid = resp.data.invoice_paid
          this.order_id = 1000000 + parseInt(this.invoice_id)
          this.invoice_status = resp.data.invoice.invoice_status
          this.invoicedetails = resp.data.invoice.invoicedetails
          this.invoicehead.push({ label: 'Invoice Number', value: resp.data.invoice.invoice_number })
          this.invoicehead.push({ label: 'Invoice Date', value: resp.data.invoice.invoice_date })
          this.invoicehead.push({ label: 'Invoice Reference', value: resp.data.invoice.invoice_reference })
          this.invoice.dibs_amount = this.invoice.invoice_amount_inc_vat.replace(',', '')
        })
    },
    // Save the invoice information
    invoice_save () {
      const invoice = {
        invoice_reference: this.invoice_reference,
        invoice_name: this.invoice_name,
        invoice_company: this.invoice_company,
        invoice_address_1: this.invoice_address_1,
        invoice_address_2: this.invoice_address_2,
        invoice_zip_code: this.invoice_zip_code,
        invoice_city: this.invoice_city,
        invoice_status: 2,
        invoice_payment_method_id: this.payment,
        invoice_company_regno: this.invoice_company_regno,
        e_invoice: this.e_invoice,
        invoice_email: this.invoice_email
        // coupon_id: this.coupon_id,
      }
      this.$store.dispatch('groupsurveyinvoice_save', { invoice_id: this.invoice_id, invoice })
        .then(() => {
          this.report_paid = 1
          this.invoice_status = 2
          this.$nextTick(() => {
          // this.$store.dispatch('groupsurveyinvoice_notifyadmin', { invoice_id: this.invoice_id })
            this.invoice_paid = 1
            this.makereport()
          })
        })
    },
    invoice_updateinfo () {
      const invoice = {
        invoice_reference: this.invoice_reference,
        invoice_name: this.invoice_name,
        invoice_company: this.invoice_company,
        invoice_address_1: this.invoice_address_1,
        invoice_address_2: this.invoice_address_2,
        invoice_zip_code: this.invoice_zip_code,
        invoice_city: this.invoice_city,
        invoice_company_regno: this.invoice_company_regno,
        e_invoice: this.e_invoice,
        invoice_email: this.invoice_email
      }
      this.$store
        .dispatch('groupsurveyinvoice_save', {
          invoice_id: this.invoice_id,
          invoice
        })
        .then(() => { this.checkform() })
    },
    receipt_download () {
      this.$store
        .dispatch('gdqagreport_receipt_create', { invoice_id: this.invoice_id })
        .then(resp => {
          const byteCharacters = atob(resp.data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          const fileURL = window.URL.createObjectURL(blob)
          const fileLink = document.createElement('a')
          fileLink.href = fileURL
          fileLink.setAttribute(
            'download',
            'GDQ_Receipt_' +
              this.invoice_id +
              '_' +
              new Date().toISOString().split('T')[0]
          )
          document.body.appendChild(fileLink)
          fileLink.click()
        })
    },
    // Get the report from state and DB
    report_get () {
      // We are accessing a previously created report
      if (this.report_id !== 0) {
        this.$store.dispatch('record_read', { table_name: 'g_agreports', id: this.report_id })
          .then((resp) => {
            this.report_name = resp.data.report_name
            this.surveytype_id = resp.data.surveytype_id
            this.normvalues_id = resp.data.normvalues_id
            this.language_id = resp.data.language_id
            const reportsel = resp.data.report_selections
            const sel = JSON.parse(reportsel)
            this.dateperiods = sel.dateperiods
            this.invoice_get()
            this.step = 5
            this.report_loaded = 1
          })
      }
    },
    // ----------------------------
    // Create the report in the DB
    // ----------------------------
    savereport () {
      const data = {
        report_name: this.report_name,
        surveytype_id: this.surveytype_id,
        normvalues_id: this.normvalues_id,
        language_id: this.language_id,
        dateperiods: this.dateperiods,
        report_active: 0
      }
      this.$store.dispatch('gdqagreport_save', data)
        .then((res) => {
          this.report_id = res.data.id
          this.$store.dispatch('setcurrentaggregatedreport_id', { report_id: this.report_id })
          this.invoice_get()
        })
    },
    canpayInvoice () {
      return this.profile && (this.profile.can_pay_invoice === 1 || this.profile.can_pay_invoice === '1')
    },
    checkform () {
      this.formok = true
      if (this.invoice_name === '') {
        this.formok = false
      }
      if (this.invoice_company === '') {
        this.formok = false
      }
      if (this.invoice_company_regno === '') {
        this.formok = false
      }
      if (this.invoice_accept === false) {
        this.formok = false
      }
      if (this.invoice_reference === '') {
        this.formok = false
      }
    },
    // -------------------------------------------------------
    // Creates the report PowerPoint and updates the database
    // -------------------------------------------------------
    async makereport () {
      const data = {
        report_id: this.report_id
      }
      let action = 'gdqagreport_create'
      if (Number(this.surveytype_id) === 2) {
        action = 'gdqagshortreport_create'
      }
      return await this.$store.dispatch(action, data)
        .then(resp => {
          this.report_id = resp.data.id
          this.report_paid = 1
          this.report_loaded = 1
        })
    },
    report_download () {
      const reporturl = config.report_url + '/gdq_agreport.php?r=' + this.report_id
      const downloadURL = new URL(reporturl)
      const downloadLink = document.createElement('a')
      downloadLink.href = downloadURL
      downloadLink.setAttribute('download', 'GDQ_AGReport_' + new Date().toISOString().split('T')[0])
      document.body.appendChild(downloadLink)
      downloadLink.click()
    },
    stepnotcomplete () {
      const surveyTypeId = Number(this.surveytype_id)
      if (
        (this.step === 1 && (surveyTypeId === 0 || this.normvalues_id === 0 || this.language_id === 0)) ||
        (this.step === 2 && this.surveyselectcount === 0) ||
        (this.step === 3 && this.dateperiods.length === 0)
      ) {
        return true
      }
      return false
    },
    adddateperiod () {
      if (this.dateperiod_label !== '') {
        const period = {
          id: this.dateperiods.length + 1,
          label: this.dateperiod_label,
          surveys: []
        }
        this.dateperiods.push(period)
        this.dateperiod_label = ''
      }
    },
    removedateperiod (id) {
      const period = this.dateperiods.filter(p => p.id === id)[0]
      period.surveys.forEach(s => {
        s.in_timeslot = 0
      })
      const index = this.dateperiods.map(e => e.id).indexOf(id)
      this.dateperiods.splice(index, 1)
    },
    marktimeslottedsurveys (start_date, end_date) {
      this.surveys.forEach(s => {
        if (s.survey_end_date >= start_date && s.survey_end_date <= end_date && Number(s.selected) === 1) {
          s.in_timeslot = 1
        }
      })
    },
    surveytypes_get () {
      this.surveytypes = []
      const filtertypes = []
      filtertypes.push(2)
      filtertypes.push(1)
      const filtervalue = filtertypes.toString()
      const filter = {
        id: {
          operator: 'IN',
          value: filtervalue
        }
      }
      this.$store.dispatch('records_readall', { table_name: 'g_surveytypes', filter, orderby: 'surveytype_name' })
        .then((resp) => {
          this.surveytypes = resp.data.result.map((r) => { return { value: r.id, text: r.surveytype_name } })
          if (this.surveytypes.length === 1) {
            this.surveytype_id = this.surveytypes[0].value
          }
        })
    },
    normvalues_countries_get: function () {
      const surveyTypeId = Number(this.surveytype_id)
      this.$store.dispatch('normvalues_countries_fetch')
        .then((resp) => {
          this.normvalues = resp.data.map((n) => {
            return {
              value: n.country_id,
              text: n.country_name
            }
          })
            .filter((n) => n.value === '999' || surveyTypeId === 1)
          if (this.normvalues.length === 1) {
            this.normvalues_id = this.normvalues[0].value
          }
        })
    },
    language_options_get () {
      const surveyTypeId = Number(this.surveytype_id)
      const filter = {}
      switch (surveyTypeId) {
        case 1:
          filter.language_enabled = {
            operator: '=',
            value: '1'
          }
          break
        case 2:
          filter.language_enabled_gdq_short = {
            operator: '=',
            value: '1'
          }
          break
        case 3:
          filter.language_enabled_osc = {
            operator: '=',
            value: '1'
          }
          break
        default:
          filter.language_enabled = {
            operator: '=',
            value: '1'
          }
      }
      this.$store.dispatch('records_readall', { table_name: 'g_languages', filter, orderby: 'language_name' })
        .then((resp) => {
          this.languages = resp.data.result.map((l) => {
            return {
              value: l.id,
              text: l.language_name
            }
          })
            .filter((n) => {
              return (n.value === '154' || n.value === '38') || surveyTypeId === 1
            })
          if (this.languages.length === 1) {
            this.language_id = this.languages[0].value
          }
        })
      return true
    },
    isOtherConsultantSurvey (survey) {
      const ownerEmail = survey && survey.user_id ? survey.user_id : ''
      const currentEmail = this.$store.state.user && this.$store.state.user.email ? this.$store.state.user.email : ''
      if (ownerEmail && currentEmail && ownerEmail !== currentEmail) {
        return true
      }
      const ownerId = survey && survey._user_id !== undefined && survey._user_id !== null && survey._user_id !== ''
        ? Number(survey._user_id)
        : (survey && survey.consultant_id !== undefined && survey.consultant_id !== null && survey.consultant_id !== ''
            ? Number(survey.consultant_id)
            : null)
      if (ownerId !== null) {
        return ownerId !== Number(this.consultant_id)
      }
      return false
    },
    surveyOwnerTooltip (survey) {
      const ownerEmail = survey && survey.user_id ? survey.user_id : ''
      const ownerId = survey && survey._user_id ? survey._user_id : (survey && survey.consultant_id ? survey.consultant_id : '')
      if (ownerEmail && ownerId) {
        return `${ownerEmail} (${ownerId})`
      }
      return ownerEmail || ownerId
    },
    surveys_get () {
      this.loading = true
      const filter = {
        user_id: {
          operator: 'IN',
          value: this.consultantsingroupids.join(','),
          realfield: 1
        },
        survey_status_id: {
          operator: '==',
          value: 4
        },
        survey_deleted: {
          operator: '==',
          value: 0
        },
        survey_type_id: {
          operator: '==',
          value: this.surveytype_id
        }
      }
      axios.get(config.base_url + '/gdq_surveys_get?filter=' + JSON.stringify(filter))
        .then((resp) => {
          this.surveys = resp.data.result.map(s => { s.selected = 0; s.in_timeslot = 0; return s })
          this.surveyRows = this.surveys.length
          this.loading = false
        })
    },
    surveys_countselected () {
      this.$nextTick(() => {
        let surveyselectcount = 0
        this.shownormvalueserror = false
        for (let i = 0; i < this.surveys.length; i++) {
          if (this.surveys[i].survey_normvalues_id !== this.normvalues_id && Number(this.surveys[i].selected) === 1) {
            this.surveys[i].selected = 0
            this.shownormvalueserror = true
          }
          if (Number(this.surveys[i].selected) === 1) {
            surveyselectcount++
          }
        }
        this.surveyselectcount = surveyselectcount
      })
    },
    prevstep () {
      this.step--
      if (this.step === 0) {
        this.$router.push('/')
      }
    },
    nextstep () {
      this.step++
      if (this.step === 2) {
        this.surveys_get()
      }
      if (this.step === 3) {
        const thesurveys = this.surveys.filter(s => Number(s.selected) === 1)
        const min = thesurveys.reduce(function (prev, current) {
          return (prev.survey_end_date < current.survey_end_date) ? prev : current
        })
        const max = thesurveys.reduce(function (prev, current) {
          return (prev.survey_end_date > current.survey_end_date) ? prev : current
        })
        this.select_start_date = min.survey_start_date
        this.select_end_date = max.survey_end_date
      }
      if (this.step === 5) {
        this.savereport()
      }
    }
  }
}
</script>
<style scoped>
.gh400 {
  height: 400px !important;
  overflow-y: scroll !important;
}
.gh300 {
  height: 300px !important;
  overflow-y: scroll !important;
}
.line-through {
    text-decoration: line-through;
}
.survey-type-badge {
  background-color: #007d8f !important;
}
.survey-icon,
.survey-type-badge .fa-icon {
  color: #ffffff !important;
  fill: #ffffff !important;
  opacity: 1 !important;
  width: 36px;
  height: 36px;
}
.user-indicator {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 1.5rem;
}
.user-indicator--placeholder {
  visibility: hidden;
}
.select-th,
.select-td {
  width: 3rem;
  vertical-align: middle !important;
}
.select-td .form-check {
  min-height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.select-td .form-check-label {
  display: inline-flex;
  align-items: center;
  padding-left: 1.5rem;
}
.select-td .form-check-label::before,
.select-td .form-check-label::after {
  top: 50%;
  transform: translateY(-50%);
}
.card--flat {
  border-radius: 0;
}
.card--flat .card-header,
.card--flat .card-body {
  border-radius: 0;
}
.measurement-card {
  max-width: 40rem;
  width: 100%;
}
.card--flat .card-body {
  padding-left: 0;
  padding-right: 0;
}
</style>
